import React, { useEffect, useMemo, useState } from 'react';

import { api } from '../../api/client';
import { API_ITEMS_URL } from '../../utils';
import { normalizeId } from '../../utils/ids';
import { createLookupResolver } from '../../utils/lookups';
import { useCardDefinitions } from '../../contexts/CardDefinitionsContext';
import useLinkedItems from '../../hooks/useLinkedItems';
import { ItemCard } from './ItemCardsGrid';

// How many recent items to look through for one belonging to the form's
// person. The API has no per-person filter; a short newest-first page keeps
// the fetch cheap and covers the common run of entries for one person.
const PER_PERSON_LOOKBACK = 5;

/** Does this item's lookup element point at `personValue`? Multi-value
 *  lookups match when any entry does. */
const matchesPerson = (item, elementName, personValue) => {
	const wanted = normalizeId(personValue);
	if (!wanted) return false;
	const held = item?.elements?.[elementName];
	const entries = Array.isArray(held) ? held : [held];
	return entries.some(entry => normalizeId(entry) === wanted);
};

/**
 * The entry form's context card: the most recent saved item of the same
 * card type in this collection, so rapid repeat capture ("what was the
 * last reading?") never needs a second tab. Renders nothing when there is
 * no previous item.
 *
 * With `personElementName` + `personValue` (the form's first card-lookup
 * and its current value) it prefers the newest item for THAT person —
 * "Last reading — Molly" — falling back to the newest item overall when
 * none of the recent ones is hers.
 */
export default function LastItemCard({ collectionId, cardId, personElementName, personValue }) {
	const { cardsById, getElementsForCard, loading: cardsLoading } = useCardDefinitions();
	const [lastItem, setLastItem] = useState(null);

	const normalizedCardId = normalizeId(cardId);
	const normalizedPerson = normalizeId(Array.isArray(personValue) ? personValue[0] : personValue) || '';
	const byPerson = Boolean(personElementName && normalizedPerson);

	useEffect(() => {
		if (!collectionId || !normalizedCardId) return;
		let active = true;
		api.get(API_ITEMS_URL, {
			params: {
				collection_id: collectionId,
				card_id: normalizedCardId,
				limit: byPerson ? PER_PERSON_LOOKBACK : 1,
				skip: 0,
				sort_field: 'createdAt',
				sort_direction: 'desc',
			},
		})
			.then(({ data }) => {
				if (!active) return;
				const rows = Array.isArray(data) ? data : [];
				const own = byPerson ? rows.find(row => matchesPerson(row, personElementName, normalizedPerson)) : null;
				setLastItem(own || rows[0] || null);
			})
			.catch(() => { /* the form works without the context card */ });
		return () => { active = false; };
	}, [collectionId, normalizedCardId, byPerson, personElementName, normalizedPerson]);

	const seed = useMemo(() => (lastItem ? [lastItem] : []), [lastItem]);
	const itemsById = useLinkedItems({
		items: seed,
		getElementsForCard,
		enabled: !cardsLoading,
		resetKey: normalizedCardId,
	});

	const resolveLookupValue = useMemo(() => createLookupResolver({
		itemsById,
		cardsById,
		getElementsForCard,
	}), [itemsById, cardsById, getElementsForCard]);

	if (!lastItem) return null;

	const card = cardsById?.[normalizedCardId] || null;
	const cardType = card?.label?.singular || card?.name || 'item';

	return (
		<section className="form-panel item-form-last">
			<div className="micro-label form-panel__kicker">
				Last {cardType}
			</div>
			<ItemCard
				item={lastItem}
				card={card}
				elementDefs={getElementsForCard(normalizedCardId) || []}
				resolveLookupValue={resolveLookupValue}
				readOnly
			/>
		</section>
	);
}
